import { useState, useMemo } from 'react';
import { useMutation } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ArrowRightLeft, Check, Send, MessageCircle, AlertCircle, Scale } from 'lucide-react';
import { api } from '../lib/api';
import { flagFor } from '../lib/flags';
import { useI18n } from '../lib/i18n';

type Sticker = {
  id: string;
  number: number;
  code: string | null;
  player_name: string | null;
  team: string | null;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  receiver: { id: string; displayName: string; city: string | null; avatarUrl: string | null };
  myStickers: Sticker[];          // mi colección completa (para mapear number → id)
  iCanGiveNumbers: number[];      // mis repes que el otro necesita
  iCanReceiveNumbers: number[];   // sus repes que yo necesito
  score: number;
  onSent: () => void;
  demoMode?: boolean;             // si true, no llama al backend, solo simula
};

export function TradeModal({
  isOpen, onClose, receiver, myStickers, iCanGiveNumbers, iCanReceiveNumbers, score, onSent, demoMode,
}: Props) {
  const { t } = useI18n();
  const stickerByNumber = useMemo(() => {
    const m = new Map<number, Sticker>();
    for (const s of myStickers) m.set(s.number, s);
    return m;
  }, [myStickers]);

  const offerCandidates = useMemo(
    () => iCanGiveNumbers.map((n) => stickerByNumber.get(n)).filter(Boolean) as Sticker[],
    [iCanGiveNumbers, stickerByNumber],
  );
  const requestCandidates = useMemo(
    () => iCanReceiveNumbers.map((n) => stickerByNumber.get(n)).filter(Boolean) as Sticker[],
    [iCanReceiveNumbers, stickerByNumber],
  );

  // Por default todas seleccionadas
  const [offer, setOffer] = useState<Set<string>>(new Set());
  const [request, setRequest] = useState<Set<string>>(new Set());
  const [message, setMessage] = useState('');

  // Inicializar cuando abre o cambian los candidatos
  useMemo(() => {
    setOffer(new Set(offerCandidates.map((s) => s.id)));
    setRequest(new Set(requestCandidates.map((s) => s.id)));
    setMessage('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, offerCandidates.length, requestCandidates.length]);

  const sendMut = useMutation({
    mutationFn: async (token: string) => {
      if (demoMode) {
        await new Promise((r) => setTimeout(r, 600));
        return { tradeId: 'demo' };
      }
      return api.proposeTrade({
        receiverId: receiver.id,
        offerStickerIds: [...offer],
        requestStickerIds: [...request],
        message: message.trim() || undefined,
        score,
      }, token);
    },
    onSuccess: () => {
      onSent();
    },
  });

  if (!isOpen) return null;

  const token = JSON.parse(localStorage.getItem('pannini.auth') || '{}').token;
  const offerCount = offer.size;
  const requestCount = request.size;
  const ratio = offerCount && requestCount
    ? Math.max(offerCount, requestCount) / Math.min(offerCount, requestCount)
    : 1;
  const isUnfair = ratio >= 3 && offerCount > 0 && requestCount > 0;
  const canSend = offerCount > 0 && requestCount > 0;

  function toggle(set: Set<string>, setter: (s: Set<string>) => void, id: string) {
    const next = new Set(set);
    next.has(id) ? next.delete(id) : next.add(id);
    setter(next);
  }
  function selectAll(candidates: Sticker[], setter: (s: Set<string>) => void) {
    setter(new Set(candidates.map((s) => s.id)));
  }
  function clearAll(setter: (s: Set<string>) => void) {
    setter(new Set());
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 z-50 bg-ink/70 backdrop-blur-sm grid place-items-end sm:place-items-center p-0 sm:p-4"
      >
        <motion.div
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 40, opacity: 0 }}
          transition={{ type: 'spring', damping: 28, stiffness: 280 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-lg max-h-[92dvh] overflow-y-auto rounded-t-3xl sm:rounded-3xl glass-strong border border-line/60 p-5 space-y-4 relative"
        >
          {/* Header */}
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-2xs uppercase tracking-widest text-haze">{t('tradeModal.eyebrow')}</p>
              <h2 className="font-display font-extrabold text-2xl mt-0.5 truncate">
                {t('tradeModal.with')} {receiver.displayName.split(' ')[0]}
              </h2>
              {receiver.city && (
                <p className="text-xs text-haze mt-0.5">{receiver.city}</p>
              )}
            </div>
            <button
              onClick={onClose}
              className="shrink-0 grid h-9 w-9 place-items-center rounded-full bg-void/60 border border-line text-haze hover:text-foam hover:bg-void transition"
              aria-label={t('common.close')}
            >
              <X size={16} />
            </button>
          </div>

          {/* Score chip */}
          <div className="flex items-center gap-2">
            <span className={`chip text-2xs font-bold ${
              score >= 0.8 ? 'text-lime border-lime/40 bg-lime/10' :
              score >= 0.6 ? 'text-cyan border-cyan/40 bg-cyan/10' :
              ''
            }`}>
              {Math.round(score * 100)}% {t('tradeModal.match')}
            </span>
            <span className="text-2xs text-haze">
              {offerCount + requestCount} {offerCount + requestCount === 1 ? t('tradeModal.sticker') : t('tradeModal.stickers')} {t('tradeModal.inPlay')}
            </span>
          </div>

          {/* Yo doy */}
          <TradeSection
            title={t('tradeModal.iGiveLabel')}
            subtitle={`${offerCount} ${t('tradeModal.iGiveDesc')}`}
            accent="lime"
            items={offerCandidates}
            selected={offer}
            onToggle={(id) => toggle(offer, setOffer, id)}
            onAll={() => selectAll(offerCandidates, setOffer)}
            onNone={() => clearAll(setOffer)}
            allLabel={t('tradeModal.all')}
            noneLabel={t('tradeModal.none')}
            emptyLabel={t('tradeModal.noItems')}
          />

          {/* Yo recibo */}
          <TradeSection
            title={t('tradeModal.iGetLabel')}
            subtitle={`${requestCount} ${t('tradeModal.iGetDesc')}`}
            accent="cyan"
            items={requestCandidates}
            selected={request}
            onToggle={(id) => toggle(request, setRequest, id)}
            onAll={() => selectAll(requestCandidates, setRequest)}
            onNone={() => clearAll(setRequest)}
            allLabel={t('tradeModal.all')}
            noneLabel={t('tradeModal.none')}
            emptyLabel={t('tradeModal.noItems')}
          />

          {/* Balance warning */}
          {isUnfair && (
            <div className="flex items-start gap-2 rounded-xl border border-flame/40 bg-flame/10 p-3 text-sm">
              <Scale size={16} className="mt-0.5 shrink-0 text-flame" />
              <div className="text-flame">
                {t('tradeModal.unfair')} ({offerCount}:{requestCount})
              </div>
            </div>
          )}

          {/* Mensaje */}
          <div className="rounded-2xl border border-line/60 bg-ink/40 p-3">
            <div className="flex items-center gap-2 text-2xs uppercase tracking-widest font-bold text-haze mb-2">
              <MessageCircle size={11} /> {t('tradeModal.messageLabel')}
            </div>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={t('tradeModal.messagePlaceholder')}
              rows={2}
              maxLength={280}
              className="w-full bg-transparent text-base placeholder-haze/60 outline-none resize-none"
            />
            <div className="text-right text-2xs text-haze">{message.length}/280</div>
          </div>

          {sendMut.isError && (
            <div className="flex items-start gap-2 rounded-xl border border-flame/40 bg-flame/10 p-3 text-sm">
              <AlertCircle size={16} className="mt-0.5 shrink-0 text-flame" />
              <div className="text-flame">{(sendMut.error as Error)?.message}</div>
            </div>
          )}

          {/* Footer actions */}
          <div className="flex gap-2 sticky bottom-0 -mx-1 pb-1">
            <button className="btn-ghost flex-1" onClick={onClose} disabled={sendMut.isPending}>
              {t('common.cancel')}
            </button>
            <button
              className="btn-primary flex-1"
              onClick={() => token && sendMut.mutate(token)}
              disabled={!canSend || sendMut.isPending}
            >
              {sendMut.isPending ? t('common.sending') : <><Send size={14} /> {t('tradeModal.send')}</>}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function TradeSection({
  title, subtitle, accent, items, selected, onToggle, onAll, onNone,
  allLabel, noneLabel, emptyLabel,
}: {
  title: string;
  subtitle: string;
  accent: 'lime' | 'cyan';
  items: Sticker[];
  selected: Set<string>;
  onToggle: (id: string) => void;
  onAll: () => void;
  onNone: () => void;
  allLabel: string;
  noneLabel: string;
  emptyLabel: string;
}) {
  const colors = accent === 'lime'
    ? { text: 'text-lime', border: 'border-lime/40', bg: 'bg-lime/10', glow: 'shadow-[0_0_18px_-6px_rgba(198,255,94,0.45)]' }
    : { text: 'text-cyan', border: 'border-cyan/45', bg: 'bg-cyan/10', glow: 'shadow-[0_0_18px_-6px_rgba(94,224,255,0.5)]' };
  return (
    <div>
      <div className="flex items-center justify-between mb-2 px-1">
        <div>
          <div className={`text-2xs uppercase tracking-widest font-bold ${colors.text}`}>{title}</div>
          <div className="text-2xs text-haze mt-0.5">{subtitle}</div>
        </div>
        <div className="flex gap-1 text-2xs">
          <button onClick={onAll} className="px-2 py-0.5 rounded-full bg-white/5 text-haze hover:text-foam hover:bg-white/10 transition">{allLabel}</button>
          <button onClick={onNone} className="px-2 py-0.5 rounded-full bg-white/5 text-haze hover:text-foam hover:bg-white/10 transition">{noneLabel}</button>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        {items.length === 0 && (
          <div className="col-span-full glass rounded-xl p-3 text-center text-2xs text-haze">
            {emptyLabel}
          </div>
        )}
        {items.map((s) => {
          const isSelected = selected.has(s.id);
          const code = s.code?.replace(/-/g, ' ') ?? `#${s.number}`;
          const flag = flagFor(s.team);
          return (
            <button
              key={s.id}
              onClick={() => onToggle(s.id)}
              className={`relative rounded-xl p-3 text-left transition ${
                isSelected
                  ? `${colors.bg} border ${colors.border} text-foam ${colors.glow}`
                  : 'bg-void/40 border border-line text-haze'
              }`}
            >
              <div className="flex items-center justify-between">
                <span className={`font-mono font-extrabold text-sm tabular-nums ${isSelected ? colors.text : 'line-through opacity-60'}`}>
                  {code}
                </span>
                {isSelected ? (
                  <Check size={14} className={`${colors.text} shrink-0`} />
                ) : (
                  <X size={14} className="text-haze shrink-0" />
                )}
              </div>
              {(s.player_name || s.team) && (
                <div className={`mt-1 text-2xs line-clamp-1 flex items-center gap-1 ${isSelected ? 'text-haze' : 'text-haze/60'}`}>
                  {flag && <span>{flag}</span>}
                  {s.player_name ?? s.team}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// VS arena icon (decorative)
export function VsIcon() {
  return (
    <div className="grid h-10 w-10 place-items-center rounded-full border border-line bg-void">
      <ArrowRightLeft size={16} className="text-flame" />
    </div>
  );
}
